import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ChevronRight, Quote, Phone, MapPin, Calendar, Tag, Star } from "lucide-react";
import { loadProject, loadProjectList } from "@/lib/content";
import { caseStudySchema, breadcrumbSchema } from "@/lib/schema";
import { BUSINESS } from "@/data/content";
import useSEO from "@/hooks/useSEO";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(undefined);
  const [others, setOthers] = useState([]);

  useEffect(() => {
    loadProject(slug).then((p) => setProject(p || null));
    loadProjectList().then((list) => setOthers(list.filter((x) => x.slug !== slug).slice(0, 3)));
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [slug]);

  useSEO({
    title: project ? `${project.title} | ElectroMax Case Study` : "Project | ElectroMax",
    description: project?.description || "ElectroMax project case study.",
    image: project?.featureImage,
    type: "article",
    jsonld: project
      ? [
          caseStudySchema(project),
          breadcrumbSchema([
            { name: "Home", href: "/" },
            { name: "Projects", href: "/projects" },
            { name: project.title, href: `/projects/${project.slug}` },
          ]),
        ]
      : undefined,
  });

  if (project === undefined) {
    return <div className="pt-40 pb-32 text-white/60 mx-auto max-w-[900px] px-6" data-testid="project-loading">Loading…</div>;
  }

  if (project === null) {
    return (
      <section className="pt-40 pb-32 min-h-[70vh]" data-testid="project-not-found">
        <div className="mx-auto max-w-[900px] px-6 text-center">
          <div className="text-[10px] tracking-[0.24em] uppercase text-white/50 mb-3">Project not found</div>
          <h1 className="font-display text-5xl font-black tracking-tight">This one&rsquo;s off the grid.</h1>
          <Link to="/projects" className="inline-flex mt-8 items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold px-6 py-3">
            <ArrowLeft className="w-4 h-4" /> All projects
          </Link>
        </div>
      </section>
    );
  }

  const p = project;
  const completedLabel = [p.completedMonth, p.completedYear].filter(Boolean).join(" ");
  const galleryImages = Array.isArray(p.gallery) && p.gallery.length
    ? p.gallery.map((g) => (typeof g === "string" ? g : g?.image)).filter(Boolean)
    : (p.featureImage ? [p.featureImage] : []);
  const rating = Math.max(0, Math.min(5, Number(p.review?.rating) || 0));

  return (
    <div data-testid="project-detail-page" className="bg-[#0A0A0A] text-white">
      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-12 overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-white/50 mb-8 flex-wrap">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/projects" className="hover:text-white">Projects</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white line-clamp-1">{p.title}</span>
          </div>

          <div className="text-xs tracking-[0.24em] uppercase text-[#E60000] font-bold mb-4">
            {p.serviceType}
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-5xl sm:text-6xl lg:text-[80px] leading-[0.95] tracking-tighter font-black max-w-4xl"
          >
            {p.title}
          </motion.h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed">
            {p.description}
          </p>
        </div>

        {p.featureImage && (
          <div className="mt-14 relative">
            <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
              <div className="border border-[#262626]">
                <img src={p.featureImage} alt={`${p.title} — ${p.location} · ${p.serviceType} case study by ElectroMax`} loading="eager" fetchPriority="high" className="w-full h-[420px] md:h-[620px] object-cover" />
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Meta strip */}
      <section className="border-y border-[#262626] bg-[#141414]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 grid grid-cols-2 md:grid-cols-4">
          {[
            { k: "Location", v: p.location, icon: MapPin },
            { k: "Service", v: p.serviceType, icon: null },
            { k: "Completed", v: completedLabel, icon: Calendar },
            { k: "Tags", v: (p.tags || []).slice(0, 3).join(", "), icon: Tag },
          ].filter((m) => m.v).map((m, i, arr) => (
            <div
              key={m.k}
              className={`py-6 md:py-8 px-4 md:px-6 ${
                i < arr.length - 1 ? "md:border-r border-[#262626]" : ""
              } ${i % 2 === 0 ? "border-r md:border-r-0 border-[#262626] md:border-r" : ""} ${
                i === arr.length - 1 && i % 2 === 0 ? "border-r-0" : ""
              }`}
            >
              <div className="text-[10px] tracking-[0.24em] uppercase text-white/50">{m.k}</div>
              <div className="mt-1 text-sm md:text-base font-semibold line-clamp-2">{m.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Challenge / Solution / Outcome */}
      <section className="py-24 lg:py-32 bg-[#F4F4F5] text-[#0A0A0A] border-t border-[#E4E4E7]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 grid md:grid-cols-3 gap-px bg-[#E4E4E7] border border-[#E4E4E7]">
          {[
            { h: "Challenge", p: p.challenge },
            { h: "Solution",  p: p.solution  },
            { h: "Outcome",   p: p.outcome   },
          ].map((s) => (
            <div key={s.h} data-testid={`project-${s.h.toLowerCase()}`} className="bg-white p-8 md:p-10">
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#E60000] font-bold">{s.h}</div>
              <p className="mt-4 text-sm md:text-base text-black/75 leading-relaxed whitespace-pre-line">{s.p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Gallery */}
      {galleryImages.length > 0 && (
        <section className="py-24 lg:py-32 border-t border-[#262626]">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
            <div className="text-xs tracking-[0.24em] uppercase text-white/60 mb-4">— The Build</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95] mb-12">
              Progress photos.
            </h2>
            <div className="grid md:grid-cols-2 gap-4 md:gap-6">
              {galleryImages.map((g, i) => (
                <div
                  key={i}
                  className={`border border-[#262626] overflow-hidden ${i === 0 && galleryImages.length > 1 ? "md:col-span-2" : ""}`}
                >
                  <img
                    src={g}
                    alt={`${p.title} — photo ${i + 1}`}
                    className="w-full h-full object-cover aspect-[4/3]"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonial */}
      {p.review?.quote && (
        <section className="py-24 lg:py-32 border-t border-[#262626] bg-[#0A0A0A]">
          <div className="mx-auto max-w-[1000px] px-6 md:px-10 lg:px-16 text-center">
            <Quote className="w-10 h-10 text-[#E60000] mx-auto mb-6" strokeWidth={2} />
            {rating > 0 && (
              <div className="flex items-center justify-center gap-1 mb-5" data-testid="project-review-rating">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${i < rating ? "text-[#E60000] fill-[#E60000]" : "text-white/20"}`}
                  />
                ))}
              </div>
            )}
            <blockquote className="font-display text-2xl md:text-4xl leading-tight tracking-tight">
              &ldquo;{p.review.quote}&rdquo;
            </blockquote>
            <div className="mt-8 text-sm">
              {p.review.name && <div className="font-bold">{p.review.name}</div>}
              {p.review.role && <div className="text-white/50 text-xs tracking-wide mt-1">{p.review.role}</div>}
            </div>
          </div>
        </section>
      )}

      {/* CTA + other projects */}
      <section className="py-20 border-t border-[#E4E4E7] bg-[#F4F4F5] text-[#0A0A0A]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-10">
            <div className="md:col-span-7">
              <div className="text-xs tracking-[0.24em] uppercase text-black/50 mb-3">— Similar Work</div>
              <h2 className="font-display text-3xl md:text-4xl tracking-tighter font-bold">More recent projects.</h2>
            </div>
            <div className="md:col-span-5 md:text-right flex md:justify-end gap-3">
              <Link to="/#contact" className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold px-5 py-3 text-sm">
                Start your project <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a href={BUSINESS.phoneHref} className="inline-flex items-center gap-2 border border-black/20 hover:border-black text-black font-bold px-5 py-3 text-sm">
                <Phone className="w-4 h-4" /> {BUSINESS.phone}
              </a>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/projects/${o.slug}`}
                data-testid={`related-project-${o.slug}`}
                className="group border border-[#E4E4E7] bg-white hover:border-black transition-colors overflow-hidden"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  {o.featureImage && (
                    <img src={o.featureImage} alt={`${o.title} — ${o.location} case study`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  )}
                </div>
                <div className="p-5 flex items-end justify-between">
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#E60000] font-bold">{o.serviceType}</div>
                    <div className="mt-1 font-display text-lg font-bold text-black">{o.title}</div>
                    <div className="text-xs text-black/50 mt-0.5">{o.location}</div>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-black/30 group-hover:text-[#E60000] transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
